import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/authContext';
import { PermissionsProvider } from './contexts/PermissionsContext.jsx';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';

import PublicLayout from './layouts/PublicLayout';
import LandingPage from './pages/LandingPage/LandingPage';
import SignInModal from './modals/SignInModal'; 
import SignUpModal from './modals/SignUpModal';
import AuthCallback from './auth/AuthCallback';
import supabase from './supabaseClient';
import '../styles/variables.css';
import './App.css';

// Component to handle login route
const LoginRoute = ({ onLogin, onSwitchToSignUp, error }) => {
  useEffect(() => {
    onLogin();
  }, [onLogin]);
  
  return (
    <PublicLayout onLogin={onLogin} onSignUp={onSwitchToSignUp}>
      <LandingPage />
    </PublicLayout>
  );
};

// Component to handle signup route
const SignUpRoute = ({ onSignUp, onSwitchToSignIn, error }) => {
  useEffect(() => {
    onSignUp();
  }, [onSignUp]);
  
  return (
    <PublicLayout onLogin={onSwitchToSignIn} onSignUp={onSignUp}>
      <LandingPage />
    </PublicLayout>
  );
};

function App() {
  const [showSignIn, setShowSignIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [authError, setAuthError] = useState('');
  const location = useLocation();
  
  // Ensure theme attribute is initialized before any routes render
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('vlc-theme');
      const theme = saved || 'dark';
      document.documentElement.setAttribute('data-theme', theme);
      if (document.body.getAttribute('data-theme')) {
        document.body.removeAttribute('data-theme');
      }
    }
  }, []);
  
  // Reset auth error when location changes
  useEffect(() => {
    setAuthError('');
  }, [location.pathname]);

  // Toggle a class on body so only the authenticated shell (/app/*)
  // locks outer scrolling (see index.css).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    const isAuthShell = location.pathname.startsWith('/app');
    if (isAuthShell) {
      body.classList.add('auth-shell');
    } else {
      body.classList.remove('auth-shell');
    }
  }, [location.pathname]);
  
  // Handle login click
  const handleLoginClick = useCallback(() => {
    setShowSignIn(true);
    setShowSignUp(false);
    setAuthError('');
  }, []);
  
  // Handle signup click
  const handleSignUpClick = useCallback(() => {
    setShowSignUp(true);
    setShowSignIn(false);
    setAuthError('');
  }, []);

  // Handle sign in
  const handleSignIn = async (email, password) => {
    try {
      // Make sure email and password are strings
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.toString()
      });
      
      if (error) throw error;
      
      setShowSignIn(false);
      // Redirect to the authenticated area
      window.location.href = '/app/home';
    } catch (error) {
      console.error("Login error:", error);
      setAuthError(error.message);
    }
  };

  // Handle sign up
  const handleSignUp = async (formData) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
            display_name: formData.displayName,
            phone: formData.phoneNumber,
            age: formData.age,
            gender: formData.gender,
            location: formData.location,
            occupation: formData.occupation,
            native_languages: formData.nativeLanguages.map(lang => lang.value),
            target_languages: formData.targetLanguages.map(lang => lang.value),
            bio: formData.bio,
          }
        }
      });
      
      if (error) throw error;
      
      setShowSignUp(false);
    } catch (error) {
      setAuthError(error.message);
    }
  };

  // Protected route component
  const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) return <div className="loading">Loading...</div>;
    
    if (!user) {
      return <Navigate to="/" replace />;
    }
    
    return children;
  };

  return (
    <AuthProvider>
      <PermissionsProvider>
        <div className="app">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={
              <PublicLayout onLogin={handleLoginClick} onSignUp={handleSignUpClick}>
                <LandingPage />
              </PublicLayout>
            } />
            
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Login and Signup routes */}
            <Route 
              path="/login" 
              element={
                <LoginRoute 
                  onLogin={handleLoginClick}
                  onSwitchToSignUp={handleSignUpClick}
                  error={authError}
                />
              } 
            />
            <Route 
              path="/signup" 
              element={
                <SignUpRoute 
                  onSignUp={handleSignUpClick}
                  onSwitchToSignIn={handleLoginClick}
                  error={authError}
                />
              } 
            />
            
            {/* Add a redirect for /dashboard to /app/home */}
            <Route path="/dashboard" element={<Navigate to="/app/home" replace />} />
            
            {/* Protected routes */}
            {/* Default authenticated routes */}
            <Route path="/app/*" element={
              <ProtectedRoute>
                <AuthenticatedLayout />
              </ProtectedRoute>
            } />
            
            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Modals */}
          <SignInModal 
            open={showSignIn} 
            onClose={() => {
              setShowSignIn(false);
              setAuthError('');
            }}
            onSignIn={handleSignIn}
            error={authError}
            onSwitchToSignUp={handleSignUpClick}
          />
          
          <SignUpModal 
            open={showSignUp} 
            onClose={() => {
              setShowSignUp(false);
              setAuthError('');
            }}
            onSignUp={handleSignUp}
            error={authError}
            onSwitchToSignIn={handleLoginClick}
          />
        </div>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
